import React, { useState, useEffect } from 'react';
import '../App.css';

// 인트로 캐러셀 아이템 데이터
const introItems = [
  {
   title: "질문은 익명으로",
    description: "질문할때 신경쓰였던 시선, \n 이제 익명으로 안녕입니다.",
    emoji: "🕵️"
  },
  {
    title: "막힘없는 환경",
    description: "어디서든지 고민하지 마세요. \n 휴대폰과 컴퓨터 모든 환경에서 가능하니까요.",
    emoji: "📱"
  },
  {
    title: "지금 시작해보세요",
    description: "실시간 질문과 답변, \n 학습의 질을 높여줍니다.",
    emoji: "💬"
  }
];

export default function Intro({ onLogin }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isKakaoInitialized, setIsKakaoInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // 모바일 화면 크기 감지
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 카카오 SDK 초기화 확인
  useEffect(() => {
    const kakaoScript = document.createElement('script');
    kakaoScript.src = 'https://developers.kakao.com/sdk/js/kakao.js';
    kakaoScript.async = true;
    kakaoScript.onload = () => {
      if (window.Kakao && !window.Kakao.isInitialized()) {
        // 카카오 SDK 초기화 - JavaScript 키 적용
        window.Kakao.init('df3c2d9beb7e0d746dcf619c7809cc73');
        console.log('카카오 SDK 초기화 성공:', window.Kakao.isInitialized());
        setIsKakaoInitialized(true);
      }
    };
    document.head.appendChild(kakaoScript);

    return () => {
      if (document.head.contains(kakaoScript)) {
        document.head.removeChild(kakaoScript);
      }
    };
  }, []);

  // 자동 슬라이드 기능
  useEffect(() => {
    if (isPaused) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % introItems.length);
    }, 4000); // 4초마다 변경
    
    return () => clearInterval(interval);
  }, [isPaused]);

  // 수동 슬라이드 변경 핸들러
  const handleSlideChange = (index) => {
    setCurrentIndex(index);
    // 슬라이드 변경 시 잠시 자동 슬라이드를 멈춤
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 4000);
  };

  // 다음/이전 슬라이드 인덱스 계산
  const nextIndex = (currentIndex + 1) % introItems.length;
  const prevIndex = (currentIndex - 1 + introItems.length) % introItems.length;

  // 카카오 로그인 처리
  const handleKakaoLogin = () => {
    if (!window.Kakao || !window.Kakao.isInitialized()) {
      alert('카카오 SDK가 초기화되지 않았습니다.');
      return;
    }
    
    setIsLoading(true);

    window.Kakao.Auth.login({
      success: (authObj) => {
        window.Kakao.API.request({
          url: '/v2/user/me',
          success: (res) => {
            const kakaoId = 'kakao_' + res.id;
            const kakaoNick = res.kakao_account?.profile?.nickname || 
                             res.properties?.nickname || 
                             '카카오 사용자';
            
            const userData = { 
              id: kakaoId, 
              nickname: kakaoNick,
              isAnonymous: false
            };
            
            console.log('카카오 로그인 성공:', userData);
            onLogin(userData);
            setIsLoading(false);
          },
          fail: (err) => {
            console.error('사용자 정보 요청 실패:', err);
            alert('사용자 정보를 불러오는데 실패했습니다.');
            setIsLoading(false);
          }
        });
      },
      fail: (err) => {
        console.error('카카오 로그인 실패:', err);
        alert('카카오 로그인에 실패했습니다.');
        setIsLoading(false);
      }
    });
  };

  // 임시 카카오 로그인 (개발용)
  const handleTempKakaoLogin = () => {
    setIsLoading(true);
    
    // 개발 테스트용 임시 로그인
    setTimeout(() => {
      // 로컬스토리지에서 이전에 사용했던 사용자 ID가 있는지 확인
      let userId;
      try {
        const savedData = JSON.parse(localStorage.getItem('tempUserData'));
        userId = savedData?.id || 'user_' + Math.random().toString(36).substring(2, 10);
      } catch {
        userId = 'user_' + Math.random().toString(36).substring(2, 10);
      }
      
      const userData = {
        id: userId,
        nickname: '카카오 사용자',
        isAnonymous: false
      };
      
      // 임시 사용자 ID 저장 (로그아웃 후에도 동일 ID로 로그인 가능하도록)
      localStorage.setItem('tempUserData', JSON.stringify({ id: userId }));
      
      console.log('임시 로그인 시도:', userData);
      onLogin(userData);
      setIsLoading(false);
    }, 800);
  };

  // 카드의 위치와 스타일을 계산하는 함수
  const getCardStyle = (index) => {
    if (index === currentIndex) {
      return {
        opacity: 1,
        transform: 'translateX(0) scale(1)',
        zIndex: 30,
        filter: 'blur(0px)',
        transition: 'all 0.6s cubic-bezier(0.25, 0.1, 0.25, 1.0)'
      };
    } else if (index === nextIndex) {
      return {
        opacity: 0.7,
        transform: 'translateX(15%) scale(0.9)',
        zIndex: 20,
        filter: 'blur(1px)',
        transition: 'all 0.6s cubic-bezier(0.25, 0.1, 0.25, 1.0)'
      };
    } else if (index === prevIndex) {
      return {
        opacity: 0.7,
        transform: 'translateX(-15%) scale(0.9)',
        zIndex: 20,
        filter: 'blur(1px)',
        transition: 'all 0.6s cubic-bezier(0.25, 0.1, 0.25, 1.0)'
      };
    } else {
      return {
        opacity: 0,
        transform: 'translateX(0) scale(0.8)',
        zIndex: 10,
        filter: 'blur(2px)',
        transition: 'all 0.6s cubic-bezier(0.25, 0.1, 0.25, 1.0)'
      };
    }
  };

  return (
    <div className="intro-container" style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: 'linear-gradient(135deg, #f6f9fc 0%, #ecf0f9 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* 배경 장식 요소 */}
      <div style={{
        position: 'absolute',
        width: '500px',
        height: '500px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(99, 102, 241, 0.05) 0%, rgba(99, 102, 241, 0.01) 70%, transparent 100%)',
        top: '-100px',
        right: '-100px',
        zIndex: 0
      }}></div>
      <div style={{
        position: 'absolute',
        width: '300px',
        height: '300px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(99, 102, 241, 0.05) 0%, rgba(99, 102, 241, 0.01) 70%, transparent 100%)',
        bottom: '10%',
        left: '-50px',
        zIndex: 0
      }}></div>

      {/* 모바일 타이틀 및 로고 */}
      <div style={{
        padding: isMobile ? '1.5rem 1rem' : '2rem',
        textAlign: 'center',
        position: 'relative',
        zIndex: 10
      }}>
        <h1 style={{
          fontSize: isMobile ? '2rem' : '2.5rem',
          fontWeight: 700,
          margin: '0',
          color: '#4f46e5',
          background: 'linear-gradient(to right, #4f46e5, #6366f1)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}>
          ezQnA
        </h1>
        <p style={{
          fontSize: isMobile ? '1rem' : '1.2rem',
          color: '#6b7280',
          margin: '0.5rem 0 0 0',
        }}>
          익명으로 툭, 답변은 쏙
        </p>
      </div>

      {/* 캐러셀 컨테이너 */}
      <div 
        style={{
          position: 'relative',
          width: '100%',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'hidden',
          padding: '2rem 0',
          zIndex: 10
        }}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onTouchStart={() => setIsPaused(true)}
        onTouchEnd={() => setTimeout(() => setIsPaused(false), 4000)}
      >
        {/* 캐러셀 아이템 */}
        <div style={{
          width: '100%',
          maxWidth: '500px',
          position: 'relative',
          height: '320px',
          perspective: '1000px'
        }}>
          {introItems.map((item, index) => (
            <div 
              key={index}
              style={{
                position: 'absolute',
                width: '100%',
                padding: '0 1.5rem',
                ...getCardStyle(index),
                pointerEvents: index === currentIndex ? 'auto' : 'none'
              }}
            >
              <div style={{
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                borderRadius: '1.2rem',
                padding: '2rem',
                boxShadow: '0 10px 30px rgba(99, 102, 241, 0.12)',
                textAlign: 'center',
                border: '1px solid rgba(255, 255, 255, 0.8)',
                transition: 'transform 0.3s ease',
                transform: index === currentIndex ? 'translateY(0)' : 'translateY(10px)',
              }}>
                <div 
                  style={{
                    fontSize: '3.5rem',
                    marginBottom: '1rem',
                    animation: index === currentIndex ? 'bounce 2s infinite' : 'none'
                  }}
                >
                  {item.emoji}
                </div>
                <h2 style={{
                  fontSize: '1.5rem',
                  fontWeight: 600,
                  color: '#1e293b',
                  margin: '0 0 1rem 0',
                }}>
                  {item.title}
                </h2>
                <p style={{
                  fontSize: '1rem',
                  color: '#64748b',
                  margin: '0',
                  lineHeight: 1.6,
                  whiteSpace: 'pre-line'
                }}>
                  {item.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* 슬라이드 컨트롤 */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: '1.5rem',
          gap: '0.75rem',
        }}>
          {/* 이전 버튼 */}
          <button
            onClick={() => handleSlideChange((currentIndex - 1 + introItems.length) % introItems.length)}
            style={{
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'white',
              border: 'none',
              borderRadius: '50%',
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
              cursor: 'pointer',
              transition: 'transform 0.2s, background 0.2s',
            }}
            onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
            aria-label="이전 슬라이드"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>

          {/* 인디케이터 */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '0.5rem',
          }}>
            {introItems.map((_, index) => (
              <button
                key={index}
                onClick={() => handleSlideChange(index)}
                style={{
                  width: index === currentIndex ? '24px' : '8px',
                  height: '8px',
                  borderRadius: '4px',
                  background: index === currentIndex ? '#6366f1' : '#cbd5e1',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  transition: 'all 0.3s ease',
                }}
                aria-label={`슬라이드 ${index + 1}`}
              />
            ))}
          </div>

          {/* 다음 버튼 */}
          <button
            onClick={() => handleSlideChange((currentIndex + 1) % introItems.length)}
            style={{
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'white',
              border: 'none',
              borderRadius: '50%',
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
              cursor: 'pointer',
              transition: 'transform 0.2s, background 0.2s',
            }}
            onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
            aria-label="다음 슬라이드"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        </div>
      </div>

      {/* 로그인 버튼 영역 */}
      <div style={{
        padding: isMobile ? '2rem 1.5rem' : '2rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '1rem',
        background: 'white',
        boxShadow: '0 -4px 10px rgba(0, 0, 0, 0.05)',
        zIndex: 10,
        position: 'relative'
      }}>
        <button
          onClick={isKakaoInitialized ? handleKakaoLogin : handleTempKakaoLogin}
          disabled={isLoading}
          style={{
            width: '100%',
            maxWidth: '320px',
            padding: '1rem',
            background: '#FEE500',
            color: '#3C1E1E',
            border: 'none',
            borderRadius: '0.75rem',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            boxShadow: '0 2px 10px rgba(254, 229, 0, 0.2)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '0.5rem',
            transition: 'transform 0.2s, box-shadow 0.2s',
            opacity: isLoading ? 0.7 : 1,
          }}
          onMouseOver={(e) => {
            if (!isLoading) {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(254, 229, 0, 0.25)';
            }
          }}
          onMouseOut={(e) => {
            if (!isLoading) {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 10px rgba(254, 229, 0, 0.2)';
            }
          }}
        >
          {isLoading ? (
            <>
              <div style={{ 
                width: '18px', 
                height: '18px', 
                border: '2px solid rgba(60, 30, 30, 0.2)',
                borderTop: '2px solid #3C1E1E',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <span style={{ marginLeft: '8px' }}>로그인 중...</span>
            </>
          ) : (
            <>
              <svg width="20" height="18" viewBox="0 0 20 18" fill="#3C1E1E">
                <path d="M10 0C4.48 0 0 3.28 0 7.32c0 2.56 1.71 4.82 4.3 6.12-.19.58-.68 2.1-.78 2.43-.12.4.15.4.32.28.13-.08 2.1-1.42 2.96-2C7.8 14.7 8.9 14.75 10 14.75c5.52 0 10-3.28 10-7.32C20 3.28 15.52 0 10 0z"/>
              </svg>
              카카오로 시작하기
            </>
          )}
        </button>
        <p style={{
          margin: '0.5rem 0 0 0',
          fontSize: '0.8rem',
          color: '#9ca3af',
          textAlign: 'center',
        }}>
          공주대학교 소프트웨어학과 민선홍
          <br />
          devminsh02@gmail.com
        </p>
      </div>

      {/* 애니메이션 스타일 추가 */}
      <style jsx="true">{`
        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% {
            transform: translateY(0);
          }
          40% {
            transform: translateY(-10px);
          }
          60% {
            transform: translateY(-5px);
          }
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
} 